import { ref, Ref } from '@vue/composition-api';
import tips from '@/renderer/scripts/utils/tips';
import { getCheckManagementPermission } from '@/renderer/scripts/utils/UserRole';
import smh, { SMH } from '@/renderer/scripts/smh';
import { formatSize } from '@/renderer/scripts/utils/tools/index';
import store from '@/renderer/store';

type ListTeamParams = {
  teamId: number | string;
  withPath?: boolean;
  withNoSpaceTeams?: 0 | 1;
};

type Handler = {
  keyword: Ref<string>;
  page: Ref<number>;
  orgSpaceSize: Ref<string>;
  orgSpace: Ref<string>;
  orgAvailableSpace: Ref<string>;
  listTeam: (params: ListTeamParams) => Promise<SMH.TeamTreeNode>;
  listTeamSpace: (teamId: number) => Promise<SMH.QueryTeamSpace>;
  createTeam: (teamInfo: SMH.TeamInfo) => Promise<SMH.CreateTeamData>;
  editTeam: (teamId: number, { name, spaceQuotaSize }: SMH.TeamInfo, isNewOpenSpace: boolean) => Promise<void>;
  deleteTeam: (teamId: number, ToTeamId: number | string, ToDirPath: string) => Promise<void>;
  getOrgSpaceInfo: (currentQutoa: number) => Promise<void>;
};

/**
 * @description
 * @export
 * @param {boolean} [checkPermission=0] 是否仅列出所属团队
 * @param {boolean} [needCheckManagementPermission=false] 是否仅列出管辖团队
 * @return {*}  {Handler}
 */
export default function teamHandler(checkPermission: 0 | 1 = 0, needCheckManagementPermission = false): Handler {
  const keyword = ref('');
  const page = ref(1);
  const orgSpaceSize = ref('-1');
  const orgSpace = ref('');
  const orgAvailableSpace = ref('0');
  const methods = {
    /**
     * @description 查询团队信息
     * @param {number} [teamId] 要查询的团队信息
     * @param {boolean} [withPath=false] 返回值是否要带路径
     * @return {*}
     */
    async listTeam({ teamId, withPath, withNoSpaceTeams }: ListTeamParams) {
      const res = {
        id: -1,
        name: '',
        parentId: -1,
        children: [],
        userCount: 0,
        spaceId: '',
      };
      try {
        const result = await smh.userAPI.queryTeam({
          teamId,
          withPath,
          checkPermission,
          ...(needCheckManagementPermission && { checkManagementPermission: await getCheckManagementPermission() }),
          // 团队管理里默认查询无空间的团队 with_no_space_teams需传1
          ...(withNoSpaceTeams && { withNoSpaceTeams }),
        });
        return result.data;
      } catch (e) {
        tips.error(e as MsgError);
      }
      return res;
    },
    // 查询团队空间
    async listTeamSpace(teamId: number) {
      const result = {
        libraryId: '',
        spaceId: '',
        capacity: '',
        availableSpace: '',
        size: '',
      };
      try {
        const res = await smh.userAPI.listTeamSpace({
          teamId,
        });
        return res.data;
      } catch (e) {
        tips.error(e as MsgError);
      }
      return result;
    },
    // 新增团队
    async createTeam({ name, parentId, spaceQuotaSize, defaultRoleId, noSpace }: SMH.TeamInfo) {
      const res = { id: 0 };
      try {
        let spaceSize = '';
        // noSpace === true时 spaceSize必为''
        if (!noSpace) {
          spaceSize = spaceQuotaSize === null ? '' : spaceQuotaSize;
        }
        const { data } = await smh.userAPI.createTeam({
          name,
          parentId,
          spaceQuotaSize: spaceSize,
          defaultRoleId,
          ...(noSpace && { noSpace }),
        });
        res.id = data.id;
      } catch (e) {
        tips.error(e as MsgError);
        throw e;
      }
      return res;
    },
    // 编辑团队
    async editTeam(teamId: number, { name, spaceQuotaSize, defaultRoleId }: SMH.TeamInfo, isNewOpenSpace: boolean) {
      try {
        const spaceSize = spaceQuotaSize === null ? '' : spaceQuotaSize;
        // 原本是noSpace的团队重新打开了空间并分配了配额
        const openSpace = isNewOpenSpace;
        await smh.userAPI.updateTeam({
          teamId,
          name,
          spaceQuotaSize: spaceSize,
          defaultRoleId,
        });
        await smh.userAPI.setTeamSpace({
          teamId,
          capacity: spaceSize,
          ...(openSpace && { openSpace: isNewOpenSpace }),
        });
      } catch (e) {
        tips.error(e as MsgError);
        throw e;
      }
    },
    // 删除团队
    async deleteTeam(teamId: number, toTeamId: number | string, toDirPath: string) {
      try {
        await smh.userAPI.deleteTeam({
          teamId,
          moveSpaceToTeam: toTeamId,
          moveSpaceToDir: toDirPath,
        });
      } catch (e) {
        tips.error(e as MsgError);
        throw e;
      }
    },
    // 查询组织配额
    async getOrgSpaceInfo(currentQutoa: number) {
      try {
        const { availableSpace } = await store.dispatch('GetOrgSpace');
        const realAvaQutoa = +availableSpace + currentQutoa; // 可用配额 = 剩余的配额 + 用户当前配额
        orgSpaceSize.value = `${realAvaQutoa}`;
        const sizeInfo = formatSize(orgSpaceSize.value);
        orgSpace.value = `${sizeInfo.size}${sizeInfo.unit}`;
        orgAvailableSpace.value = availableSpace;
      } catch (e) {
        orgSpaceSize.value = '0';
        orgSpace.value = '0MB';
        orgAvailableSpace.value = '0';
      }
    },
  };
  return {
    keyword,
    page,
    orgSpaceSize,
    orgSpace,
    orgAvailableSpace,
    ...methods,
  };
}
